// ============================================================
// Dashboard Page
// ============================================================

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Briefcase, Users, ClipboardCheck, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export function DashboardPage() {
    const { data: stats } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: () => api.get('/dashboard/stats'),
    });

    const { data: jobsData } = useQuery({
        queryKey: ['jobs-recent'],
        queryFn: () => api.get('/jobs?limit=5'),
    });

    const s = stats?.data || { totalJobs: 0, totalCandidates: 0, totalAssessments: 0 };
    const recentJobs = jobsData?.data || [];

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Dashboard</h1>
                    <p className="subtitle">Welcome back — here's your hiring overview</p>
                </div>
                <Link to="/jobs" className="btn btn-primary">
                    <Briefcase size={16} /> Create Job
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="card-grid card-grid-4 mb-6">
                <div className="stat-card">
                    <div className="flex items-center gap-3">
                        <div style={{ padding: 8, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 8 }}>
                            <Briefcase size={20} color="#6366f1" />
                        </div>
                        <div className="stat-label">Active Jobs</div>
                    </div>
                    <div className="stat-value">{s.totalJobs}</div>
                </div>

                <div className="stat-card">
                    <div className="flex items-center gap-3">
                        <div style={{ padding: 8, background: 'rgba(16, 185, 129, 0.1)', borderRadius: 8 }}>
                            <Users size={20} color="#10b981" />
                        </div>
                        <div className="stat-label">Total Candidates</div>
                    </div>
                    <div className="stat-value" style={{ background: 'linear-gradient(135deg, #10b981, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {s.totalCandidates}
                    </div>
                </div>

                <div className="stat-card">
                    <div className="flex items-center gap-3">
                        <div style={{ padding: 8, background: 'rgba(245, 158, 11, 0.1)', borderRadius: 8 }}>
                            <ClipboardCheck size={20} color="#f59e0b" />
                        </div>
                        <div className="stat-label">Assessments</div>
                    </div>
                    <div className="stat-value" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {s.totalAssessments}
                    </div>
                </div>

                <div className="stat-card">
                    <div className="flex items-center gap-3">
                        <div style={{ padding: 8, background: 'rgba(139, 92, 246, 0.1)', borderRadius: 8 }}>
                            <TrendingUp size={20} color="#8b5cf6" />
                        </div>
                        <div className="stat-label">Conversion Rate</div>
                    </div>
                    <div className="stat-value" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {s.conversionRate !== undefined ? `${s.conversionRate}%` : '—'}
                    </div>
                </div>
            </div>

            {/* Recent Jobs */}
            <div className="card">
                <div className="card-header">
                    <h2>Recent Jobs</h2>
                    <Link to="/jobs" className="btn btn-ghost btn-sm">
                        View All <ArrowRight size={14} />
                    </Link>
                </div>

                {recentJobs.length === 0 ? (
                    <div className="empty-state">
                        <div className="icon">📋</div>
                        <h3>No jobs yet</h3>
                        <p>Create your first job to start hiring</p>
                        <Link to="/jobs" className="btn btn-primary mt-4">Create Job</Link>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentJobs.map((job: any) => (
                                <tr key={job.id}>
                                    <td style={{ fontWeight: 600 }}>{job.title}</td>
                                    <td>
                                        <span className={`badge badge-${job.status === 'active' ? 'success' : job.status === 'draft' ? 'warning' : 'info'}`}>
                                            {job.status}
                                        </span>
                                    </td>
                                    <td style={{ color: '#6b7280' }}>{new Date(job.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <Link to={`/jobs/${job.id}`} className="btn btn-ghost btn-sm">
                                            View <ArrowRight size={14} />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
